import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ChannelError, ErrorsResponse } from '@/componentsv2/channel-product-renderer/types';
import type { ReadRequestOptions } from '../requestOptions';

type ChannelErrorRenderedValueApi = {
  original_value?: string | null;
};

type ChannelErrorAISuggestionApi = {
  id?: string | number | null;
  user_decision_status?: string | null;
  suggested_value?: string | null;
  confidence_score?: number | string | null;
  reasoning?: string | null;
  decided_at?: string | null;
  decided_by?: string | null;
  attribute_type?: string | null;
  documentation?: string | null;
};

type ChannelErrorApiItem = {
  id?: string | number | null;
  subscription_id: number;
  channel_name?: string | null;
  product_id: number;
  sku: string;
  attribute_path: string;
  error_message: string;
  error_source: string;
  created_at: string;
  updated_at?: string | null;
  original_value?: string | null;
  rendered_value?: ChannelErrorRenderedValueApi | null;
  ai_suggestion?: ChannelErrorAISuggestionApi | null;
};

type ChannelErrorsApiResponse = {
  data?: ChannelErrorApiItem[];
  meta?: ErrorsResponse['meta'];
  last_page?: number;
};

export type ChannelErrorsWithSuggestionsQuery = {
  productId?: number | string;
  page?: number;
  limit?: number;
  sort?: string;
  subscriptionId?: number | string;
  errorSource?: string;
  attributePath?: string;
  hasSuggestion?: boolean;
  decisionStatus?: 'pending' | 'accepted' | 'rejected';
  confidenceMin?: number;
  confidenceMax?: number;
};

export type ChannelErrorsWithSuggestionsResponse = ErrorsResponse & {
  last_page?: number;
};

const normalizeDecisionStatus = (status: string | null | undefined): 'pending' | 'applied' | 'rejected' => {
  if (status === 'accepted') return 'applied';
  if (status === 'rejected') return 'rejected';
  return 'pending';
};

const normalizeIdString = (v: string | number | null | undefined): string => {
  if (v === null || v === undefined) return '';
  return String(v);
};

export async function getChannelErrorsWithSuggestionsOptimized(
  scope: RequestScope,
  args: ChannelErrorsWithSuggestionsQuery,
  options: ReadRequestOptions
): Promise<ChannelApiResponse<ChannelErrorsWithSuggestionsResponse>> {
  try {
    if (
      args.productId !== undefined &&
      ((typeof args.productId === 'number' && (!Number.isInteger(args.productId) || args.productId < 1)) ||
        (typeof args.productId === 'string' && args.productId.trim() === ''))
    ) {
      throw new Error('Valid productId is required');
    }

    const params = new URLSearchParams();
    if (args.productId !== undefined) params.set('product_id', String(args.productId));
    if (args.page !== undefined) params.set('page', String(args.page));
    params.set('sort', args.sort ?? 'created_at:-1');
    params.set('limit', String(args.limit ?? 500));
    if (args.subscriptionId !== undefined) params.set('subscription_id', String(args.subscriptionId));
    if (args.errorSource) params.set('error_source', args.errorSource);
    if (args.attributePath) params.set('attribute_path', args.attributePath);
    if (args.hasSuggestion !== undefined) params.set('has_suggestion', String(args.hasSuggestion));
    if (args.decisionStatus) params.set('decision_status', args.decisionStatus);
    if (args.confidenceMin !== undefined) params.set('confidence_min', String(args.confidenceMin));
    if (args.confidenceMax !== undefined) params.set('confidence_max', String(args.confidenceMax));

    const res = await scopedChannelApi(scope).get<ChannelErrorsApiResponse>(
      `astur/integrations/channel/errors/with-suggestions-optimized?${params.toString()}`,
      options
    );
    const raw = Array.isArray(res.data?.data) ? res.data.data : [];

    const processedErrors: ChannelError[] = raw.map((item) => {
      const ai = item.ai_suggestion;
      const suggestion = ai
        ? {
            id: normalizeIdString(ai.id),
            ai_suggestion_id: normalizeIdString(ai.id) || undefined,
            subscription_id: item.subscription_id,
            product_id: item.product_id,
            sku: item.sku,
            attribute_name: item.attribute_path,
            attribute_path: item.attribute_path,
            current_value: item.rendered_value?.original_value ?? item.original_value ?? null,
            suggested_value: ai.suggested_value ?? null,
            confidence_score: ai.confidence_score ?? undefined,
            reasoning: ai.reasoning ?? undefined,
            status: normalizeDecisionStatus(ai.user_decision_status),
            applied_at: ai.decided_at ?? undefined,
            applied_by: ai.decided_by ?? undefined,
            rejected_at: ai.user_decision_status === 'rejected' ? (ai.decided_at ?? undefined) : undefined,
            rejected_by: ai.user_decision_status === 'rejected' ? (ai.decided_by ?? undefined) : undefined,
            attribute_type: ai.attribute_type ?? undefined,
            documentation: ai.documentation ?? undefined,
          }
        : undefined;

      return {
        id: normalizeIdString(item.id),
        subscription_id: item.subscription_id,
        channel_name: item.channel_name ?? undefined,
        product_id: item.product_id,
        sku: item.sku,
        attribute_path: item.attribute_path,
        error_message: item.error_message,
        error_source: item.error_source,
        created_at: item.created_at,
        updated_at: item.updated_at ?? undefined,
        original_value: item.rendered_value?.original_value ?? item.original_value ?? undefined,
        ai_suggestion: suggestion,
      };
    });

    const metaFromApi = res.data?.meta;
    const meta: ErrorsResponse['meta'] = {
      ...(metaFromApi ?? { total: processedErrors.length }),
      total: metaFromApi?.total ?? processedErrors.length,
    };

    return { data: { data: processedErrors, meta, last_page: res.data?.last_page }, status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}
